'use client';

import { useCallback, useEffect, useState } from 'react';
import { deleteCookie, getCookie, setCookie } from '~/utils/cookies';
import { useSharedRouter } from '~/utils/nextMigration/router';
import { convertToValidPartner, PARTNER_COOKIE, PARTNERS } from '~/utils/partners/constants';
import { getMiddlewareUrl } from '~/utils/server';
import { getTrackingParamsFromCookies } from '~/utils/tracking';

type Partner = (typeof PARTNERS)[keyof typeof PARTNERS];

export type PartnerCookie = keyof typeof PARTNERS | undefined;

export const getPartner = (partner: NonNullable<PartnerCookie>) => PARTNERS[partner];

export const getPartnerCookie = () => {
  return getCookie(PARTNER_COOKIE) as PartnerCookie;
};

export const getCurrentPartner = (): Partner | undefined => {
  const partner = getPartnerCookie();

  return partner ? PARTNERS[partner] : undefined;
};

export const usePartnerUtmParams = () => {
  const router = useSharedRouter();
  let { utm_source, utm_medium } = router.query;

  if (!utm_source || !utm_medium) {
    const { medium, source } = getTrackingParamsFromCookies({
      __gtm_campaign_url: getCookie('__gtm_campaign_url')
    });

    utm_source = source;
    utm_medium = medium;
  }

  const partnerSource =
    utm_medium === 'partner' && typeof utm_source === 'string' && utm_source.trim().length > 0 ? utm_source : undefined;
  const partner = convertToValidPartner(partnerSource);

  const partnerUtmSource: PartnerCookie = partner;

  return [partnerUtmSource] as const;
};

export const usePartnerCookie = () => {
  const [partnerCookie, setPartnerCookieState] = useState<PartnerCookie>(() => {
    const raw = getCookie(PARTNER_COOKIE) as string | undefined;
    return convertToValidPartner(raw);
  });

  const setPartnerCookie = useCallback(
    (candidate: string | undefined) => {
      const valid = convertToValidPartner(candidate);

      if (!valid) {
        deleteCookie(PARTNER_COOKIE);
        setPartnerCookieState(undefined);

        return;
      }

      if (valid === partnerCookie) return;

      setPartnerCookieState(valid);
      setCookie(PARTNER_COOKIE, valid, {
        maxAge: 60 * 60 * 24 * 90, // 90 days
        domain: getCookieDomain()
      });
    },
    [partnerCookie]
  );

  const deletePartnerCookie = useCallback(() => {
    deleteCookie(PARTNER_COOKIE);
    setPartnerCookieState(undefined);
  }, []);

  return [partnerCookie, setPartnerCookie, deletePartnerCookie] as const;
};

export const usePartner = () => {
  const router = useSharedRouter();
  const [mounted, setMounted] = useState(false);
  const [currentPartner, setCurrentPartner] = useState<PartnerCookie>(undefined);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    const { utm_medium, utm_source } = router.query;

    const hasPartnerUtm = utm_medium === 'partner' && typeof utm_source === 'string' && utm_source.trim().length > 0;

    const utmPartner = hasPartnerUtm ? convertToValidPartner(utm_source) : undefined;

    const cookiePartnerRaw = getCookie(PARTNER_COOKIE) as string | undefined;
    const cookiePartner = convertToValidPartner(cookiePartnerRaw);

    if (hasPartnerUtm) {
      if (!utmPartner) {
        deleteCookie(PARTNER_COOKIE);
        setCurrentPartner(undefined);
      } else {
        if (utmPartner !== cookiePartner) {
          setCookie(PARTNER_COOKIE, utmPartner, {
            maxAge: 60 * 60 * 24 * 90,
            domain: getCookieDomain()
          });
        }
        setCurrentPartner(utmPartner);
      }
    } else {
      setCurrentPartner(cookiePartner);
    }
  }, [mounted, router.query]);

  return currentPartner;
};

const getCookieDomain = () => {
  return new URL(getMiddlewareUrl({ useRelativePathOnClient: false })).hostname.replace(/^[^.]+\./g, '');
};
